import { useAuth0 } from "@auth0/auth0-react";
import React, { useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import LoadingScreen from "../../../components/loading-screen";
import PostIdentButton from "../../../components/page-components/onboarding/postident-button";
import { USER_STATE } from "../../../types/generic_types";
import { AppContext } from "../direct-router";
import { Page, Text, Grid, Card, Avatar, Input, Spacer, Divider, Button, Note } from "@geist-ui/core";
import { User, Mail } from "@geist-ui/icons";
import CountryUtils from "../../../utility/country-utils";
import { useMediaQuery } from "@mui/material";

export default function Profile() {
  const { user: auth0User } = useAuth0();
  const app_context = useContext(AppContext);
  const user = app_context.user?.natural_person;
  const isMobile = useMediaQuery('(max-width:900px)');

  if (!user) {
    return <LoadingScreen className="w-full h-screen" />;
  }

  const is_onboarded_two = app_context.userState == USER_STATE.ONBOARDED_2;
  const is_onboarded_one =
    app_context.userState == USER_STATE.ONBOARDED_1 || is_onboarded_two;
  const is_onboarded_zero =
    app_context.userState == USER_STATE.ZERO || is_onboarded_one;

  const getCountryName = (code: string | undefined) => {
      if (!code) return "";
      const country = CountryUtils.getCountries().find(c => c.alpha3 === code);
      return country ? country.name : code;
  }

  const DataField = ({ label, value }: { label: string, value: any }) => (
    <div style={{ marginBottom: '24px', width: '100%' }}>
        <Text span style={{ 
            fontSize: '11px', 
            fontWeight: 800, 
            color: '#A0A0A0', 
            textTransform: 'uppercase', 
            letterSpacing: '0.08em', 
            display: 'block', 
            marginBottom: '8px',
            fontFamily: '"Nunito Sans", sans-serif' 
        }}>
            {label}
        </Text>
        <div style={{ 
            background: '#F9F9F9', 
            border: '1px solid #F0F0F0', 
            borderRadius: '12px', 
            padding: '12px 16px', 
            fontSize: '15px', 
            fontWeight: 600, 
            color: '#888',
            fontFamily: '"Nunito Sans", sans-serif',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center'
        }}>
            {value || '-'}
        </div>
    </div>
  );

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', paddingTop: isMobile ? '100px' : '160px', paddingBottom: '100px' }}>
       <div style={{ maxWidth: '1000pt', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
            {/* Header / User Info */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: isMobile ? '60px' : '100px', gap: isMobile ? '32px' : '48px' }}>
                <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%', 
                    background: '#F5F5F5', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '1px solid #EAEAEA',
                    overflow: 'hidden',
                    flexShrink: 0
                }}>
                    {auth0User?.picture ? (
                        <img src={auth0User.picture} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Profile" />
                    ) : (
                        <Text b style={{ fontSize: '36px', color: '#666' }}>{user.forename[0]}{user.surname[0]}</Text>
                    )}
                </div>
                <div>
                     <Text h2 style={{ 
                        margin: 0, 
                        fontWeight: 900, 
                        letterSpacing: '-0.04em', 
                        lineHeight: 1, 
                        fontSize: '48px', 
                        fontFamily: '"Nunito Sans", sans-serif', 
                        color: '#000' 
                     }}>
                        {user.forename} <br/> {user.surname}
                     </Text>
                     <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#888', marginTop: '16px' }}>
                        <Mail size={18} />
                        <Text span style={{ fontSize: '16px', fontWeight: 500, fontFamily: '"Nunito Sans", sans-serif' }}>{app_context.user?.communication.email}</Text>
                     </div>
                </div>
            </div>

            {is_onboarded_zero && (
                <>
                    {/* Stammdaten */}
                    <div style={{ marginBottom: '100px' }}>
                        <Text h3 style={{ marginBottom: '48px', fontWeight: 900, letterSpacing: '-0.04em', fontSize: '32px', fontFamily: '"Nunito Sans", sans-serif', color: '#000' }}>Stammdaten</Text>
                        <Grid.Container gap={isMobile ? 1 : 3}>
                            <Grid xs={24} sm={12}>
                                <DataField label="Vorname" value={user.forename} />
                            </Grid>
                            <Grid xs={24} sm={12}>
                                <DataField label="Nachname" value={user.surname} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Geburtstag" value={user.birth_date ? new Date(user.birth_date).toLocaleDateString("de-DE") : ''} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Geburtsort" value={user.birth_place} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Geburtsland" value={getCountryName(user.birth_country)} />
                            </Grid>
                        </Grid.Container>
                    </div>

                    <div style={{ width: '100%', height: '1px', background: '#F0F0F0', marginBottom: '100px' }} />

                    {/* Adressdaten */}
                    <div style={{ marginBottom: '100px' }}>
                        <Text h3 style={{ marginBottom: '48px', fontWeight: 900, letterSpacing: '-0.04em', fontSize: '32px', fontFamily: '"Nunito Sans", sans-serif', color: '#000' }}>Adressdaten</Text>
                        <Grid.Container gap={isMobile ? 1 : 3}>
                            <Grid xs={24} sm={12}>
                                <DataField label="Straße & Hausnummer" value={user.street} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Adresszusatz" value={user.address_addition} />
                            </Grid>
                            <Grid xs={24} sm={12}>
                                <DataField label="Postleitzahl" value={user.zip} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Stadt" value={user.city} />
                            </Grid>
                             <Grid xs={24} sm={12}>
                                <DataField label="Land" value={getCountryName(user.country)} />
                            </Grid>
                        </Grid.Container>
                    </div>
                </>
            )}

             {is_onboarded_one && !is_onboarded_two && app_context.user && (
                 <>
                    <div style={{ width: '100%', height: '1px', background: '#EAEAEA', marginBottom: '64px' }} />
                    <div style={{ marginBottom: '60px' }}>
                        <Text h3 style={{ marginBottom: '24px', fontWeight: 800, letterSpacing: '-0.02em', fontSize: '22px', fontFamily: '"Nunito Sans", sans-serif' }}>Identifikation</Text>
                        
                        <div style={{ 
                            background: '#FFFFFF', 
                            border: '1px solid #EAEAEA',
                            borderRadius: '24px', 
                            padding: '48px', 
                            color: '#000',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <Text h4 style={{ color: '#000', fontWeight: 800, fontSize: '24px', marginBottom: '16px', fontFamily: '"Nunito Sans", sans-serif' }}>
                                    Legitimation erforderlich
                                </Text>
                                <Text p style={{ color: '#666', fontSize: '16px', maxWidth: '600px', lineHeight: '1.6', marginBottom: '40px', fontFamily: '"Nunito Sans", sans-serif' }}>
                                    Sie haben sich bisher noch nicht legitimiert. Um Ihre Investitionen zu finalisieren, müssen Sie das Legitimationsverfahren der Deutschen Post durchführen.
                                </Text>
                                
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                    <PostIdentButton
                                        firstName={user.forename}
                                        lastName={user.surname}
                                        email={app_context.user.communication.email}
                                        uid={app_context.user.uid}
                                        street={user.street}
                                        zip={user.zip}
                                        city={user.city}
                                        phone={user.phone}
                                        title={user.title}
                                        country={user.country}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F0F0F0' }} />
                                        Dauer: ca. 5 Min.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                 </>
             )}

       </div>
    </div>
  );
}

// Deprecated sections below can be removed as they are not used anymore in the new design
export function ProfileSection({ children }: any) { return null; }
export function ProfileSectionLeftSide({ label, children }: any) { return null; }

